import { createContext, useContext, useEffect, useState } from "react";
import { DateTime } from "luxon";
import { DeviceStatus } from "../models/device.model";
import { useDeviceStatusQuery } from "../devices/device.queries";

type DeviceStatusType = {
  [id: number]: {
    status: DeviceStatus;
    version: string | null;
    lastOnlineAt: DateTime | null;
  };
};

export const DeviceStatusContext = createContext<{
  getDeviceState: (id: number) => DeviceStatus;
  getDevice: (id: number) => {
    version: string | null;
    lastOnlineAt: DateTime | null;
  };
  getDeviceVersions: () => string[];
} | null>(null);

const UPDATE_INTERVAL = 30000;

export const DeviceStatusProvider = ({ children }) => {
  const [deviceStatusDict, setDeviceStatus] = useState<DeviceStatusType>({});
  const { data: deviceStatuses, isLoading } = useDeviceStatusQuery({
    refetchInterval: UPDATE_INTERVAL,
  });

  useEffect(() => {
    if (isLoading) return;

    /**
     * Transforming the device status array into a dictionary
     * where each key is the device ID and the value is its status.
     * This structure allows for quick lookups of a specific device's status
     * without needing to iterate through the entire array.
     */
    const ds = deviceStatuses?.reduce((acc, curr) => {
      acc[curr.id] = {
        status: curr.status,
        version: curr.version,
        lastOnlineAt: curr.lastOnlineAt,
      };
      return acc;
    }, {});
    setDeviceStatus(ds || {});
  }, [deviceStatuses, isLoading]);

  const getDeviceState = (id: number) => {
    const device = deviceStatusDict[id] || fallBackDeviceStatus;
    return device.status;
  };

  const getDevice = (id: number) => {
    const device = deviceStatusDict[id] || fallBackDeviceStatus;
    return {
      version: device.version,
      lastOnlineAt: device.lastOnlineAt,
    };
  };

  const getDeviceVersions = () => {
    return Array.from(
      Object.values(deviceStatusDict).reduce((a, c) => {
        if (c.version != null) a.add(c.version);
        return a;
      }, new Set<string>()),
    ).sort();
  };

  return (
    <DeviceStatusContext.Provider
      value={{ getDeviceState, getDevice, getDeviceVersions }}
    >
      {children}
    </DeviceStatusContext.Provider>
  );
};

export function useDeviceStateContext() {
  const value = useContext(DeviceStatusContext);
  if (!value) {
    throw new Error(
      "useDeviceStateContext has to be used under DeviceStatusProvider",
    );
  }
  return value;
}

export const fallBackDeviceStatus = {
  status: DeviceStatus.Disconnected,
  version: null,
  lastOnlineAt: null,
};
